// BentoGrid — Sprint 6 Foundation, Sprint 7 Push 2B Hero-Reihe-Erweiterung.
//
// CSS-Grid mit `grid-template-areas` fuer Desktop (>=1280px),
// Tablet-Adapt (768-1279px) und Mobile-Stack (<768px).
//
// Layout (Sprint 7 Push 2B):
//   ROW 1 (Hero-Reihe): HERO 2x2 (Cash) | kpiUmsatz 1x1 + kpiEbit 1x1 (Stack)
//                       | dsoGauge 1x1 | forderungenDonut 1x1
//   ROW 2 (Compact):    DecisionQ 2x1 | TaxMicro 1x1 | Anomaly 1x1
//   ROW 3 (Trends):     BWA-Trend 7x2 | TaxCalendar 4x2 / Mahnungen 1x2
//   ROW 4 (Per-Firma):  4 Per-Company-Cards 3x1
//
// Container-only — Kinder muessen `style={{ gridArea }}` setzen (oder via
// BentoTile-Wrapper).
//
// Tile-Areas (Naming kanonisch):
//   hero, kpiUmsatz, kpiEbit, dsoGauge, forderungenDonut,
//   decision, taxMicro, anomaly,
//   bwa, taxCalendar, mahnungen,
//   firmaDrainx, firmaGundk, firmaInfrax, firmaXholding

import { Children, cloneElement, isValidElement } from "react";
import type { CSSProperties, ReactElement, ReactNode } from "react";

import { useMediaQuery } from "../../lib/use-media-query";

export type BentoArea =
  | "hero"
  | "actions"
  | "netto"
  | "jaKompass"
  | "kpiUmsatz"
  | "kpiEbit"
  | "outflowHero"
  | "forecastHero"
  | "liquiPlanung"
  | "bwa"
  | "mahnungen"
  | "firmaDrainx"
  | "firmaGundk"
  | "firmaInfrax"
  | "firmaXholding"
  // Deprecated (HF-2.2 Sprint 7+8 Hotfix) — nicht mehr im Production-Grid,
  // bleibt im Type für die __bento_demo-Admin-Galerie. CSS-Grid ignoriert
  // unbekannte gridArea-Werte (Tile landet implizit am Grid-Ende).
  | "dsoGauge"
  | "forderungenDonut"
  | "decision"
  | "taxMicro"
  | "anomaly"
  | "taxCalendar";

// Desktop 12-col x 8-row (HF-2.2 Sprint 7+8 Hotfix: kompakte Hero-Sicht).
// Entfernt: dsoGauge, forderungenDonut, decision, taxMicro, anomaly, taxCalendar.
// Row 1-2: Hero-Reihe — Cash 6col + Umsatz/EBIT-Stack 6col.
// Row 3:   OUTFLOW HERO 12col.
// Row 4-5: FORECAST HERO 6col + LIQUI PLANUNG 6col (Phase F).
// Row 6-7: BWA 11col + Mahnungen 1col (Mahnungen 2 rows hoch).
// Row 8:   Per-Company 3col x 4.
const DESKTOP_AREAS = `
  "hero hero hero hero hero hero kpiUmsatz kpiUmsatz kpiUmsatz kpiUmsatz kpiUmsatz kpiUmsatz"
  "hero hero hero hero hero hero kpiEbit kpiEbit kpiEbit kpiEbit kpiEbit kpiEbit"
  "outflowHero outflowHero outflowHero outflowHero outflowHero outflowHero outflowHero outflowHero outflowHero outflowHero outflowHero outflowHero"
  "forecastHero forecastHero forecastHero forecastHero forecastHero forecastHero liquiPlanung liquiPlanung liquiPlanung liquiPlanung liquiPlanung liquiPlanung"
  "forecastHero forecastHero forecastHero forecastHero forecastHero forecastHero liquiPlanung liquiPlanung liquiPlanung liquiPlanung liquiPlanung liquiPlanung"
  "bwa bwa bwa bwa bwa bwa bwa bwa bwa bwa bwa mahnungen"
  "bwa bwa bwa bwa bwa bwa bwa bwa bwa bwa bwa mahnungen"
  "firmaDrainx firmaDrainx firmaDrainx firmaGundk firmaGundk firmaGundk firmaInfrax firmaInfrax firmaInfrax firmaXholding firmaXholding firmaXholding"
`;

// Tablet 8-col (HF-2.2): keine compact-row, Bento bleibt fokussiert auf
// Hero-KPIs + 4-Firmen-Tiles, Detail-Drilldown via separater Sections.
const TABLET_AREAS = `
  "hero hero hero hero kpiUmsatz kpiUmsatz kpiUmsatz kpiUmsatz"
  "hero hero hero hero kpiEbit kpiEbit kpiEbit kpiEbit"
  "outflowHero outflowHero outflowHero outflowHero outflowHero outflowHero outflowHero outflowHero"
  "forecastHero forecastHero forecastHero forecastHero forecastHero forecastHero forecastHero forecastHero"
  "liquiPlanung liquiPlanung liquiPlanung liquiPlanung liquiPlanung liquiPlanung liquiPlanung liquiPlanung"
  "bwa bwa bwa bwa bwa bwa bwa mahnungen"
  "bwa bwa bwa bwa bwa bwa bwa mahnungen"
  "firmaDrainx firmaDrainx firmaDrainx firmaDrainx firmaGundk firmaGundk firmaGundk firmaGundk"
  "firmaInfrax firmaInfrax firmaInfrax firmaInfrax firmaXholding firmaXholding firmaXholding firmaXholding"
`;

// HF-2.3 (Sprint 7+8 Hotfix): `display: grid` ist NICHT mehr inline.
// Vorher: Inline-Style `display: grid` ueberschrieb Tailwind-Klassen
// (`xl:hidden` → display:none) in der CSS-Cascade — Inline gewinnt
// IMMER gegen Klasse. Resultat: beide BentoGrid-Varianten (Tablet +
// Desktop) renderten gleichzeitig im xl-Viewport, doppelter Hero-Block
// (Kevin Extension Re-Smoke a4b2beb, Viewport 1541 px).
// Fix: `display` wird ausschliesslich ueber Tailwind-Klassen
// (`md:grid`, `xl:grid`, `hidden`) gesteuert. Die restlichen Grid-
// Properties (columns/rows/areas/gap) bleiben inline.
const DESKTOP_GRID: CSSProperties = {
  gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
  gridTemplateRows: "140px 140px 170px 110px 110px 160px 160px 180px",
  gridTemplateAreas: DESKTOP_AREAS,
  gap: "1rem",
};

const TABLET_GRID: CSSProperties = {
  gridTemplateColumns: "repeat(8, minmax(0, 1fr))",
  gridTemplateRows: "auto",
  gridAutoRows: "160px",
  gridTemplateAreas: TABLET_AREAS,
  gap: "0.75rem",
};

export interface BentoGridProps {
  children: ReactNode;
  /** Optional className fuer outer wrapper (z.B. test-id). */
  className?: string;
  /** Override desktop grid style (Sprint 10: per-tab layouts). */
  desktopStyle?: CSSProperties;
  /** Override tablet grid style (Sprint 10: per-tab layouts). */
  tabletStyle?: CSSProperties;
}

/** Stagger-Helper — fuegt staggerIndex={index} zu jedem Child hinzu damit
 *  BentoTile die Initial-Load-Animation mit korrektem Delay starten kann.
 *  Spec: 50ms pro Tile (bento-spec-sprint6.md "Animationen"). */
function withStagger(children: ReactNode): ReactNode {
  return Children.map(children, (child, index) => {
    if (!isValidElement(child)) return child;
    return cloneElement(child as ReactElement<{ staggerIndex?: number }>, {
      staggerIndex: index,
    });
  });
}

/** Container — rendert Bento auf Desktop + Tablet, Mobile faellt auf
 *  Flex-Col-Stack (Children-Reihenfolge bestimmt visual order).
 *
 *  HF-2.3 Defense-in-Depth (Sprint 7+8 Hotfix): Vorher wurden alle drei
 *  Varianten parallel ins DOM gerendert und per Tailwind-`md:hidden`/
 *  `xl:hidden` cascade-gefiltert. Das wurde durch das inline-style
 *  `display:grid` aufgehoben — beide Grids waren gleichzeitig sichtbar.
 *  Variante A (Inline-Style-Entfernung) fixt die Cascade; Variante C
 *  hier mounted ZUSAETZLICH conditional, damit nur EIN Wrapper im DOM
 *  existiert. Damit ist die Doppel-Render-Klasse strukturell ausgeschlossen,
 *  unabhaengig von CSS-Cascade-Verhalten. */
export default function BentoGrid({
  children,
  className,
  desktopStyle,
  tabletStyle,
}: BentoGridProps) {
  const isXl = useMediaQuery("(min-width: 1280px)");
  const isMd = useMediaQuery("(min-width: 768px)");
  const staggered = withStagger(children);

  if (!isMd) {
    return (
      <div className={className}>
        <div className="flex flex-col gap-3">{staggered}</div>
      </div>
    );
  }

  if (!isXl) {
    return (
      <div className={className}>
        <div className="grid" style={tabletStyle ?? TABLET_GRID}>
          {staggered}
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="grid" style={desktopStyle ?? DESKTOP_GRID}>
        {staggered}
      </div>
    </div>
  );
}
